/* ==========================================================================
   Vapor Space 2.0 — hero slideshow
   --------------------------------------------------------------------------
   Behaviour for sections/hero-slideshow.liquid. Vanilla, no dependencies,
   loaded with `defer`.

   The section renders slide 1 active and the rest stacked behind it, so the
   hero is complete and correct before this file runs. If the script never
   loads, the first slide simply stays put — the banner and its link still
   work. Nothing here is required to see the content.

   Follows the same rules as the announcement rotator in vs-core.js: never
   autoplay under prefers-reduced-motion, stop on hover, stop on focus, stop
   when the tab is hidden. It adds an explicit pause control because these
   slides carry text inside the artwork, which makes them moving text under
   WCAG 2.2.2 rather than decoration.
   ========================================================================== */

(function () {
  'use strict';

  var VS = (window.VS = window.VS || {});

  var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

  function reducedMotion() {
    if (VS.prefersReducedMotion) return VS.prefersReducedMotion();
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function init(root) {
    // The theme editor re-renders sections in place; guard so a section never
    // ends up with two sets of timers racing each other.
    if (root.dataset.vsSlideshowReady === 'true') return;
    root.dataset.vsSlideshowReady = 'true';

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
    if (slides.length < 2) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-slide-to]'));
    var prevBtn = root.querySelector('[data-slide-prev]');
    var nextBtn = root.querySelector('[data-slide-next]');
    var toggle = root.querySelector('[data-slide-toggle]');

    var index = 0;
    var timer = null;
    var interval = parseInt(root.dataset.interval, 10) || 6000;
    // Autoplay is a merchant setting AND a motion preference. Either one off
    // means it never starts.
    var wantsAutoplay = root.dataset.autoplay === 'true' && !reducedMotion();
    var paused = !wantsAutoplay;

    function setSlide(next) {
      index = (next + slides.length) % slides.length;

      slides.forEach(function (slide, i) {
        var active = i === index;

        slide.classList.toggle('is-active', active);

        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');

        // A hidden slide must also leave the tab order. Without this a
        // keyboard user tabs into a link sitting invisibly on top of the
        // visible slide, and focus vanishes.
        Array.prototype.forEach.call(slide.querySelectorAll(FOCUSABLE), function (el) {
          if (active) {
            if (el.dataset.vsTabRestore === 'true') {
              el.removeAttribute('tabindex');
              delete el.dataset.vsTabRestore;
            }
          } else if (!el.hasAttribute('tabindex')) {
            el.setAttribute('tabindex', '-1');
            el.dataset.vsTabRestore = 'true';
          }
        });
      });

      dots.forEach(function (dot, i) {
        var active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function start() {
      if (timer || paused || reducedMotion()) return;
      timer = setInterval(function () {
        setSlide(index + 1);
      }, interval);
    }

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    // A deliberate move by the visitor ends autoplay for good. Advancing out
    // from under someone who just chose a slide is the single most irritating
    // thing a carousel does.
    function userMoved(next) {
      stop();
      paused = true;
      if (toggle) {
        toggle.setAttribute('aria-pressed', 'true');
        toggle.setAttribute('aria-label', 'Play slideshow');
      }
      setSlide(next);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { userMoved(index - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { userMoved(index + 1); });

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        userMoved(parseInt(dot.dataset.slideTo, 10) || 0);
      });
    });

    if (toggle) {
      toggle.addEventListener('click', function () {
        paused = !paused;
        toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
        toggle.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
        if (paused) stop();
        else start();
      });
    }

    // Hover and focus only suspend the timer; they do not flip `paused`, so
    // moving the mouse away resumes where it left off.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    // Keyboard: arrows move between slides while focus is inside the carousel.
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        userMoved(index - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        userMoved(index + 1);
      }
    });

    // Swipe. Horizontal intent only: a mostly-vertical drag is the page being
    // scrolled and must not steal the gesture.
    var startX = null;
    var startY = null;

    root.addEventListener(
      'touchstart',
      function (e) {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      },
      { passive: true }
    );

    root.addEventListener(
      'touchend',
      function (e) {
        if (startX === null) return;
        var touch = e.changedTouches[0];
        var dx = touch.clientX - startX;
        var dy = touch.clientY - startY;
        startX = startY = null;

        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
        userMoved(dx < 0 ? index + 1 : index - 1);
      },
      { passive: true }
    );

    setSlide(0);
    start();
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-vs-slideshow]'), init);
  }

  initAll();

  // Theme editor: re-run when a section is re-rendered or reordered.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-vs-slideshow]');
    if (root) init(root);
  });
})();
